import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"

/**
 * OKS-466 — "Read the full charter" sheet for the combined signup screen
 * (UX Spec §C). Keeps the form short on mobile: the charter rich-text
 * lives behind a link that opens a bottom drawer (side panel on ≥sm).
 *
 * The charter HTML is brand-authored and server-sanitised (the BE passes
 * the same `sanitized_crew_charter` HTML the OKS-24 charter step uses), so
 * `dangerouslySetInnerHTML` is safe here — the value never carries
 * unsanitised markup (defence-in-depth scrub on every read, per OKS-40).
 *
 * Pure presentational: it does NOT own the acceptance checkbox (that lives
 * in the parent Inertia `<Form>` so the value ships natively). Closing the
 * sheet returns focus to the trigger (Radix default).
 */
export interface CharterSheetProps {
  brandName: string
  /** Server-sanitised charter HTML. */
  charterHtml: string
  testId?: string
}

export function CharterSheet({
  brandName,
  charterHtml,
  testId = "signup-charter-sheet",
}: CharterSheetProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        render={
          <button
            type="button"
            className="text-foreground self-start text-sm underline underline-offset-2"
            data-testid={`${testId}-trigger`}
          >
            {t("signups.details.charter.read_link")}
          </button>
        }
      />
      <SheetContent
        side="bottom"
        className="sm:side-right max-h-[85svh] sm:max-h-svh"
        data-testid={`${testId}-content`}
      >
        <SheetHeader>
          <SheetTitle>
            {t("signups.details.charter.sheet_title", { brand: brandName })}
          </SheetTitle>
        </SheetHeader>
        <div
          className="prose prose-sm dark:prose-invert max-w-none overflow-y-auto px-4"
          data-testid={`${testId}-body`}
          // Brand-authored + server-sanitised HTML (see component doc).
          dangerouslySetInnerHTML={{ __html: charterHtml }}
        />
        <SheetFooter>
          <Button
            type="button"
            onClick={() => setOpen(false)}
            data-testid={`${testId}-continue`}
          >
            {t("signups.details.charter.sheet_continue")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default CharterSheet
